import {
  AnyFundingRequest,
  FundingRequestProvider,
  FundingRequestType,
  IMohFormDisplay,
  IMohPreApprovalFundingRequest,
  IPatient,
  IPractice,
  isMohPreApprovalFundingRequestData,
  ITreatmentStep,
  MohFormDisplayLabel,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  AtLeast,
  IReffable,
  isWithRef,
  multiFilter,
  query$,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { Practice } from '../../practice/practice';
import { FundingRequest } from '../funding-request';

export class MohPreApprovalFundingRequest {
  static init(
    overrides: AtLeast<IMohPreApprovalFundingRequest, 'data' | 'patient'>
  ): IMohPreApprovalFundingRequest {
    return FundingRequest.init({
      fundingProvider: FundingRequestProvider.MOH,
      type: FundingRequestType.PreApproval,
      ...overrides,
    });
  }

  static all$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IMohPreApprovalFundingRequest>[]> {
    return all$(
      undeletedQuery(
        Practice.fundingRequestCol<IMohPreApprovalFundingRequest>(practice),
        where('fundingProvider', '==', FundingRequestProvider.MOH),
        where('type', '==', FundingRequestType.PreApproval)
      )
    );
  }

  static byPatient$(
    practice: IReffable<IPractice>,
    patient: IReffable<IPatient>
  ): Observable<WithRef<IMohPreApprovalFundingRequest>[]> {
    return all$(
      undeletedQuery(
        Practice.fundingRequestCol<IMohPreApprovalFundingRequest>(practice),
        where('fundingProvider', '==', FundingRequestProvider.MOH),
        where('type', '==', FundingRequestType.PreApproval),
        where('patient.ref', '==', patient.ref)
      )
    );
  }

  static byTreatmentStep$(
    practice: IReffable<IPractice>,
    step: IReffable<ITreatmentStep>
  ): Observable<WithRef<IMohPreApprovalFundingRequest>[]> {
    return query$(
      Practice.fundingRequestCol<IMohPreApprovalFundingRequest>(practice),
      where('data.stepRef', '==', step.ref)
    ).pipe(
      multiFilter(
        (fundingRequest) =>
          fundingRequest.fundingProvider === FundingRequestProvider.MOH &&
          fundingRequest.type === FundingRequestType.PreApproval &&
          !fundingRequest.deleted
      )
    );
  }

  static formDisplays(
    request: IMohPreApprovalFundingRequest
  ): IMohFormDisplay[] {
    return [
      {
        formPath: request.data.preApprovalFormPath,
        label: MohFormDisplayLabel.PreApprovalRequest,
      },
      {
        formPath: request.data.highCariesFormPath,
        label: MohFormDisplayLabel.HighCariesRequest,
      },
    ].filter((form): form is IMohFormDisplay => !!form.formPath);
  }
}

export function isMohPreApprovalFundingRequest(
  fundingRequest: WithRef<AnyFundingRequest>
): fundingRequest is WithRef<IMohPreApprovalFundingRequest> {
  return (
    isWithRef(fundingRequest) &&
    fundingRequest.fundingProvider === FundingRequestProvider.MOH &&
    fundingRequest.type === FundingRequestType.PreApproval &&
    isMohPreApprovalFundingRequestData(fundingRequest.data)
  );
}
